import React, { useEffect, useState } from 'react'

// Small header panel that shows today's weather/conditions versus the same
// weekday last year (today − 364 days, mirroring NewHomeView's LY comparison).
// Location comes from the browser's geolocation; data comes from Open-Meteo
// (same provider already used by CalendarDetailsView): the forecast endpoint
// for today and the historical archive endpoint for last year.

// Open-Meteo WMO weather codes → human label + emoji. Emoji is used instead of
// a Font Awesome <i> because FA's all.min.js rewrites <i>→<svg> by scanning the
// DOM, which is unreliable for icons that mount after an async fetch (it would
// intermittently leave one icon unrendered). Emoji renders deterministically.
function describeWeatherCode(code) {
  if (code === null || code === undefined) return { label: '—', emoji: '—' }
  if (code <= 1)  return { label: 'Despejado',   emoji: '☀️' }
  if (code === 2) return { label: 'Poco nuboso', emoji: '⛅' }
  if (code === 3) return { label: 'Nublado',     emoji: '☁️' }
  if (code <= 48) return { label: 'Niebla',      emoji: '🌫️' }
  if (code <= 57) return { label: 'Llovizna',    emoji: '🌦️' }
  if (code <= 67) return { label: 'Lluvia',      emoji: '🌧️' }
  if (code <= 77) return { label: 'Nieve',       emoji: '🌨️' }
  if (code <= 82) return { label: 'Chubascos',   emoji: '🌧️' }
  if (code <= 86) return { label: 'Nevadas',     emoji: '🌨️' }
  if (code >= 95) return { label: 'Tormenta',    emoji: '⛈️' }
  return { label: 'Nublado', emoji: '☁️' }
}

const DAILY_VARS = 'weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum'

function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const roundT = v => (v === null || v === undefined || Number.isNaN(v) ? null : Math.round(v))

function DayWeatherPanel() {
  const [coords, setCoords] = useState(null)
  const [geoError, setGeoError] = useState(false)
  const [today, setToday] = useState(null)
  const [ly, setLy] = useState(null)
  const [loading, setLoading] = useState(true)

  // Ask the browser for the current position once on mount.
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoError(true)
      setLoading(false)
      return
    }
    navigator.geolocation.getCurrentPosition(
      pos => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { setGeoError(true); setLoading(false) },
      { timeout: 10000, maximumAge: 30 * 60 * 1000 }
    )
  }, [])

  // Fetch today's + last year's weather once we know where we are.
  useEffect(() => {
    if (!coords) return
    let cancelled = false

    const run = async () => {
      setLoading(true)
      const now = new Date()
      const lyDate = new Date(now)
      lyDate.setDate(lyDate.getDate() - 364) // same weekday last year (matches the dashboard)
      const lyStr = ymd(lyDate)
      const { lat, lng } = coords

      const forecastUrl =
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
        `&current=weather_code&daily=${DAILY_VARS}` +
        `&forecast_days=1&timezone=auto`
      const archiveUrl =
        `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}` +
        `&start_date=${lyStr}&end_date=${lyStr}&daily=${DAILY_VARS}&timezone=auto`

      try {
        const [fRes, aRes] = await Promise.all([
          fetch(forecastUrl).then(r => r.json()).catch(() => null),
          fetch(archiveUrl).then(r => r.json()).catch(() => null),
        ])
        if (cancelled) return

        if (fRes && fRes.daily && fRes.daily.time && fRes.daily.time.length) {
          setToday({
            code: fRes.daily.weathercode?.[0] ?? fRes.current?.weather_code ?? null,
            tmax: fRes.daily.temperature_2m_max?.[0] ?? null,
            tmin: fRes.daily.temperature_2m_min?.[0] ?? null,
            precip: fRes.daily.precipitation_sum?.[0] ?? null,
          })
        }
        if (aRes && aRes.daily && aRes.daily.time && aRes.daily.time.length) {
          setLy({
            code: aRes.daily.weathercode?.[0] ?? null,
            tmax: aRes.daily.temperature_2m_max?.[0] ?? null,
            tmin: aRes.daily.temperature_2m_min?.[0] ?? null,
            precip: aRes.daily.precipitation_sum?.[0] ?? null,
          })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => { cancelled = true }
  }, [coords])

  if (loading) {
    return (
      <div className="kpi-weather-panel">
        <div className="kpi-weather-loading">⏳ Cargando clima…</div>
      </div>
    )
  }

  if (!today && !ly) {
    return (
      <div className="kpi-weather-panel">
        <div className="kpi-weather-error">
          {geoError ? '📍 Ubicación no disponible' : '☁️ Sin datos de clima'}
        </div>
      </div>
    )
  }

  const tDesc = describeWeatherCode(today?.code)
  const lyDesc = describeWeatherCode(ly?.code)
  // how much warmer/cooler today's high is versus the same day last year
  const tempDelta = (today?.tmax != null && ly?.tmax != null) ? (today.tmax - ly.tmax) : null

  return (
    <div className="kpi-weather-panel" title="Clima de hoy vs. mismo día de la semana del año pasado">
      {/* today */}
      <div className="kpi-weather-group">
        <span className="kpi-weather-icon" role="img" aria-label={tDesc.label}>{tDesc.emoji}</span>
        <span className="kpi-weather-day">Hoy</span>
        <span className="kpi-weather-maxmin">{roundT(today?.tmax)}° / {roundT(today?.tmin)}°</span>
        <span className="kpi-weather-cond">{tDesc.label}</span>
        {today?.precip > 0 && (
          <span className="kpi-weather-precip">💧 {today.precip}mm</span>
        )}
      </div>

      <span className="kpi-weather-sep" />

      {/* same weekday last year */}
      <div className="kpi-weather-group kpi-weather-row-ly">
        <span className="kpi-weather-icon" role="img" aria-label={lyDesc.label}>{lyDesc.emoji}</span>
        <span className="kpi-weather-day">LY</span>
        <span className="kpi-weather-maxmin">{roundT(ly?.tmax)}° / {roundT(ly?.tmin)}°</span>
        <span className="kpi-weather-cond">{lyDesc.label}</span>
        {tempDelta != null && (
          <span className={`kpi-weather-delta ${tempDelta >= 0 ? 'up' : 'down'}`}>
            {tempDelta >= 0 ? '▲' : '▼'} {Math.abs(tempDelta).toFixed(1)}°
          </span>
        )}
      </div>
    </div>
  )
}

export default DayWeatherPanel
